import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { ReconciliationLine, ReconciliationSession } from '../types'
import { fetchLineDiff } from '../api/reconciliation'
import type { DiffLine } from '../api/reconciliation'

type DocType = 'ordrsp' | 'desadv' | 'invoice'

const DOC_COLORS: Record<DocType, string> = {
  ordrsp: 'text-blue-600',
  desadv: 'text-emerald-600',
  invoice: 'text-violet-600',
}

interface Pair { a: DocType; b: DocType }

function getAvailablePairs(session: ReconciliationSession, line: ReconciliationLine): Pair[] {
  const hasOR = !!session.ordrsp_filename && !!line.ordrsp_xpath
  const hasDA = !!session.desadv_filename && !!line.desadv_xpath
  const hasINV = !!session.invoice_filename && !!line.invoice_xpath
  const pairs: Pair[] = []
  if (hasOR && hasDA)  pairs.push({ a: 'ordrsp',  b: 'desadv' })
  if (hasOR && hasINV) pairs.push({ a: 'ordrsp',  b: 'invoice' })
  if (hasDA && hasINV) pairs.push({ a: 'desadv',  b: 'invoice' })
  return pairs
}

function rowBg(status: string): string {
  if (status === 'changed') return 'bg-yellow-50'
  if (status === 'added')   return 'bg-green-50'
  if (status === 'removed') return 'bg-red-50'
  return ''
}

function cellColor(status: string, side: 'left' | 'right'): string {
  if (status === 'equal')   return 'text-gray-700'
  if (status === 'changed') return side === 'left' ? 'text-red-700' : 'text-green-700'
  if (status === 'added')   return side === 'right' ? 'text-green-700' : 'text-gray-200'
  if (status === 'removed') return side === 'left'  ? 'text-red-700'  : 'text-gray-200'
  return 'text-gray-700'
}

interface Props {
  session: ReconciliationSession
  line: ReconciliationLine
}

export function XmlDiffView({ session, line }: Props) {
  const { t } = useTranslation()
  const pairs = getAvailablePairs(session, line)
  const [activePair, setActivePair] = useState<Pair | null>(pairs[0] ?? null)
  const [diffLines, setDiffLines] = useState<DiffLine[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const DOC_LABELS: Record<DocType, string> = {
    ordrsp: 'OrderResponse',
    desadv: 'DespatchAdvice',
    invoice: t('xml_diff_view.doc_label_invoice'),
  }

  useEffect(() => {
    if (!activePair) return
    setLoading(true)
    setError(null)
    setDiffLines(null)
    fetchLineDiff(session.id, line.id, activePair.a, activePair.b)
      .then(result => setDiffLines(result.lines))
      .catch(e => setError(e instanceof Error ? e.message : t('xml_diff_view.error_loading_diff')))
      .finally(() => setLoading(false))
  }, [session.id, line.id, activePair])

  if (pairs.length === 0) {
    return (
      <p className="text-xs text-gray-400 italic">
        {t('xml_diff_view.no_pairs_available')}
      </p>
    )
  }

  const hasDiffs = diffLines?.some(l => l.status !== 'equal')

  return (
    <div className="space-y-3">
      {/* Paar-kiezer */}
      <div className="flex flex-wrap gap-2">
        {pairs.map(pair => {
          const active = activePair?.a === pair.a && activePair?.b === pair.b
          return (
            <button
              key={`${pair.a}-${pair.b}`}
              onClick={() => setActivePair(pair)}
              className={`px-3 py-1 rounded-lg text-xs font-medium border transition-colors ${
                active
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
            >
              {DOC_LABELS[pair.a]} ↔ {DOC_LABELS[pair.b]}
            </button>
          )
        })}
      </div>

      {/* Legenda */}
      <div className="flex gap-4 text-[10px] text-gray-400">
        <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-100 mr-1" />{t('xml_diff_view.legend_left_changed')}</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-green-100 mr-1" />{t('xml_diff_view.legend_right_changed')}</span>
        <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-yellow-100 mr-1" />{t('xml_diff_view.legend_both_changed')}</span>
      </div>

      {/* Diff tabel */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Kolomkoppen */}
        <div className="grid grid-cols-2 bg-gray-50 border-b border-gray-200">
          <div className={`px-3 py-2 text-xs font-semibold border-r border-gray-200 ${activePair ? DOC_COLORS[activePair.a] : ''}`}>
            {activePair ? DOC_LABELS[activePair.a] : ''}
          </div>
          <div className={`px-3 py-2 text-xs font-semibold ${activePair ? DOC_COLORS[activePair.b] : ''}`}>
            {activePair ? DOC_LABELS[activePair.b] : ''}
          </div>
        </div>

        <div style={{ maxHeight: '420px' }} className="overflow-auto">
          {loading && (
            <div className="p-4 text-xs text-gray-400 text-center">{t('xml_diff_view.loading_diff')}</div>
          )}
          {error && (
            <div className="p-4 text-xs text-red-500 text-center">{error}</div>
          )}
          {!loading && !error && diffLines?.length === 0 && (
            <div className="p-4 text-xs text-gray-400 text-center">
              {t('xml_diff_view.element_not_found')}
            </div>
          )}
          {!loading && !error && diffLines && diffLines.length > 0 && (
            <>
              {!hasDiffs && (
                <div className="px-3 py-2 text-xs text-green-700 bg-green-50 border-b border-green-100 font-medium">
                  {t('xml_diff_view.no_differences')}
                </div>
              )}
              <pre className="text-xs font-mono leading-5 select-text">
                {diffLines.map((row, i) => (
                  <div key={i} className={`grid grid-cols-2 ${rowBg(row.status)}`}>
                    <div className={`px-2 py-0.5 border-r border-gray-200 min-w-0 truncate ${cellColor(row.status, 'left')}`}>
                      {row.left ?? <span className="opacity-30 select-none">∅</span>}
                    </div>
                    <div className={`px-2 py-0.5 min-w-0 truncate ${cellColor(row.status, 'right')}`}>
                      {row.right ?? <span className="opacity-30 select-none">∅</span>}
                    </div>
                  </div>
                ))}
              </pre>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
